export interface ChartDataPoint {
    label: string;
    value: number;
}

export interface ChartDataSeries {
    name: string | null;
    data: ChartDataPoint[];
}

export interface Chart {
    series: ChartDataSeries[];
    min?: number | null;
    max?: number | null;
    median?: number | null;
    average?: number | null;
    /** Horizontal reference value (e.g. PuDo SLOT capacity on occupancy charts). */
    reference?: number | null;
}

/** Formats API chart labels/series names that may be strings or SLOT dimension objects. */
export const formatChartLabel = (value: unknown): string => {
    if (value == null) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const width = record['width'];
        const height = record['height'];
        const depth = record['depth'];
        if (width != null && height != null && depth != null) {
            return `${width}×${height}×${depth}`;
        }

        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }

    return String(value);
};

const normalizeOptionalNumber = (value: number | null | undefined): number | null | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (value === null) {
        return null;
    }

    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/** Ensures chart consumers always receive an array series with string labels and numeric values. */
export const normalizeChart = (chart: Chart): Chart => ({
    series: (chart.series ?? []).map((series) => ({
        name: series?.name == null ? null : formatChartLabel(series.name),
        data: Array.isArray(series?.data)
            ? series.data.map((point) => ({
                  label: formatChartLabel(point?.label),
                  value: typeof point?.value === 'number' ? point.value : 0
              }))
            : []
    })),
    min: normalizeOptionalNumber(chart.min),
    max: normalizeOptionalNumber(chart.max),
    median: normalizeOptionalNumber(chart.median),
    average: normalizeOptionalNumber(chart.average),
    reference: normalizeOptionalNumber(chart.reference)
});
